import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowLeft, 
    faCopy, 
    faCheckCircle, 
    faCheck, 
    faFileAlt, 
    faBullhorn, 
    faQuestionCircle,
    faClock,
    faPhone,
    faEnvelope,
    faUser,
    faShieldAlt,
    faPaperPlane
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import "./CSS/nameChange.css";

const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let captcha = "";
    for (let i = 0; i < 6; i++) {
        captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return captcha;
};

export const NameChange = () => {
    const navigate = useNavigate();
    const formRef = useRef(null);
    
    // States for Form
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: ""
    });
    
    const [copiedTextId, setCopiedTextId] = useState(null);
    const [activeTab, setActiveTab] = useState("general");
    const [activeFaq, setActiveFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.phone || !formData.email || !formData.message) {
            alert("Please fill in all the required fields.");
            return;
        }

        const countryCode = "+91";
        const phoneNumber = "9500037429";
        const text = `*Newspaper Name Change Ad Inquiry*\n\n` +
                     `*Name:* ${formData.name}\n` +
                     `*Phone:* ${formData.phone}\n` +
                     `*Email:* ${formData.email}\n` +
                     `*Draft / Details:* ${formData.message}`;
                     
        const whatsappUrl = `https://wa.me/${countryCode}${phoneNumber}?text=${encodeURIComponent(text)}`;
        alert("Thank you! Redirecting you to our ad specialist on WhatsApp for instant quote & draft selection...");
        window.open(whatsappUrl, "_blank");
        
        // Reset form
        setFormData({
            name: "",
            email: "",
            phone: "",
            message: ""
        });
    };

    const handleWhatsAppDirect = () => {
        const countryCode = "+91";
        const phoneNumber = "9500037429";
        const text = "Hi, I want to book a Name Change Ad in the newspaper. Please guide me on the rates and documents needed.";
        const whatsappUrl = `https://wa.me/${countryCode}${phoneNumber}?text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, "_blank");
    };

    const heroRef = useRef(null);

    // Smooth Scroll to hero section
    const scrollToForm = () => {
        heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Copy to clipboard helper
    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedTextId(id);
            setTimeout(() => setCopiedTextId(null), 2000);
        }).catch(err => {
            console.error("Failed to copy text: ", err);
        });
    };

    // Ad Templates content
    const templates = {
        general: {
            title: "General Name Change",
            text: "I, Amit Sharma S/o Rajesh Sharma R/o 12, Park Avenue, Chennai - 600018, Tamil Nadu, have changed my name to Amit Kumar Sharma for all future purposes.",
        },
        marriage: {
            title: "Post Marriage (Minors/Women)",
            text: "I, Priya G. D/o Govindasamy W/o Vigneshwaran R/o 45, Anna Salai, Chennai - 600002, have changed my name to Priya Vigneshwaran for all future purposes.",
        },
        correction: {
            title: "Spelling Correction",
            text: "I, Srinivasulu R. S/o Ramulu R/o 89, Nehru Street, Madurai - 625001, state that Srinivasulu R. and Srinivasa Rao R. refer to one and the same person. Henceforth, I shall be known as Srinivasulu R. for all purposes.",
        },
        minor: {
            title: "Minor's Name Change",
            text: "I, Rakesh V. R/o 56, Gandhi Road, Coimbatore - 641002, parent of Govind V. (Minor, DOB: 12-05-2015) have changed my son's name to Govindan V. for all future purposes.",
        }
    };

    const toggleFaq = (index) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    const faqs = [
        {
            question: "What information is required to book a Change of Name advertisement?",
            answer: "To book your advertisement, we generally require your old name, new name, father’s / husband’s name (S/o, D/o, or W/o), date of birth, place of birth, residential address, and mobile number. Our team will guide you if any additional information is required."
        },
        {
            question: "Which documents are required for a Change of Name advertisement?",
            answer: "The document requirements vary from newspaper to newspaper. Some newspapers may require a Notary Affidavit, while others may have different requirements. Our team will inform you of the exact requirements before booking your advertisement."
        },
        {
            question: "Is Gazette publication mandatory?",
            answer: "Gazette publication is not mandatory in every case. The requirement depends on the purpose of your name change and the authority where you intend to use your updated name. Our team will guide you on the appropriate process based on your requirement."
        },
        {
            question: "Can I book my Change of Name advertisement online?",
            answer: "Yes. You can book your advertisement by phone, WhatsApp, email, or by submitting the enquiry form on this page. Our team will assist you throughout the booking process."
        },
        {
            question: "Which newspaper should I choose for my Change of Name advertisement?",
            answer: "The most suitable newspaper depends on your purpose and location. Our team will recommend the appropriate newspaper and edition based on your specific requirement."
        },
        {
            question: "How soon can my advertisement be published?",
            answer: "If your booking is completed before the newspaper’s booking deadline and all required details are provided, your advertisement can usually be published in the earliest available edition. Our team will confirm the publication date at the time of booking."
        },
        {
            question: "Will I receive a copy of the published advertisement?",
            answer: "Yes. We can provide the published newspaper copy and/or e-paper copy, depending on the newspaper’s availability."
        },
        {
            question: "How much does a Change of Name newspaper advertisement cost?",
            answer: "The cost depends on the newspaper, edition, advertisement size, and language. Contact us for a free quotation, and we'll provide the best available rates."
        },
        {
            question: "Can I publish my advertisement in Tamil or English?",
            answer: "Yes. We can help you publish your Change of Name advertisement in English, Tamil, and other regional languages, depending on the newspaper you choose."
        }
    ];

    return (
        <div className="name-change-wrapper">
            <Helmet>
                <title>Newspaper Name Change Advertisement Services - Sri Sairam Advertising</title>
                <meta name="keywords" content="Newspaper name change ad, Name change advertisement, Passport name change, Gazette publication, Name correction ads list, Chennai ad agency, Sri Sairam Advertising" />
                <meta name="description" content="Book Name Change advertisement in leading newspapers easily with Sri Sairam Advertising. Get affidavit formats, templates, and low cost bookings." />
            </Helmet>

            {/* PREMIUM HERO SECTION - Google Ads Landing Page Style */}
            <header className="nc-hero" ref={heroRef}>
                <div className="nc-hero-overlay"></div>

                {/* Back button inside the hero - subtle */}
                <button className="nc-back-btn" onClick={() => navigate("/service/newspaper")}>
                    ← Newspaper Services
                </button>

                <div className="nc-hero-inner">
                    <div className="nc-hero-left">
                        <p className="nc-hero-badge">✦ Legal · Fast · Trusted Since 1988</p>
                        <p className="nc-hero-category">CHANGE OF NAME · NEWSPAPER ADVERTISEMENT</p>
                        <h1 className="nc-hero-title">
                            Publish your<br />
                            <span className="nc-hero-title-highlight">Change of Name</span><br />
                            advertisement in<br />
                            leading newspapers.
                        </h1>
                        <div className="nc-hero-actions">
                            <button className="nc-hero-btn-primary" onClick={scrollToForm}>
                                Get instant quote &nbsp; ➔
                            </button>
                            <button className="nc-hero-btn-secondary" onClick={handleWhatsAppDirect}>
                                Chat on WhatsApp
                            </button>
                        </div>
                    </div>
                    <div className="nc-hero-right">
                        <div className="nc-hero-form-card" ref={formRef}>
                            {/* Form Header */}
                            <div className="nc-form-header">
                                <span className="nc-form-badge">
                                    <FontAwesomeIcon icon={faClock} /> FAST 2-MINUTE AD BOOKING
                                </span>
                                <h3 className="nc-form-title">Publish Your Name Change Notice Today</h3>
                                <p className="nc-form-subtitle">
                                    Fill out the details below. Our ad specialist will instantly contact you with best rates & sample drafts.
                                </p>
                            </div>
                            
                            <form className="nc-booking-form" onSubmit={handleFormSubmit}>
                                {/* Full Name */}
                                <div className="nc-form-field">
                                    <label htmlFor="name">Your Name <span className="req">*</span></label>
                                    <div className="nc-input-wrapper">
                                        <span className="nc-input-icon"><FontAwesomeIcon icon={faUser} /></span>
                                        <input 
                                            type="text" 
                                            id="name" 
                                            name="name" 
                                            placeholder="Enter your full name" 
                                            value={formData.name} 
                                            onChange={handleChange}
                                            required 
                                        />
                                    </div>
                                </div>

                                {/* Contact Row: Phone & Email */}
                                <div className="nc-form-row">
                                    <div className="nc-form-field">
                                        <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                                        <div className="nc-input-wrapper">
                                            <span className="nc-input-icon"><FontAwesomeIcon icon={faPhone} /></span>
                                            <input 
                                                type="tel" 
                                                id="phone" 
                                                name="phone" 
                                                placeholder="10-digit mobile number" 
                                                value={formData.phone} 
                                                onChange={handleChange}
                                                required 
                                            />
                                        </div>
                                    </div>

                                    <div className="nc-form-field">
                                        <label htmlFor="email">Email Address <span className="req">*</span></label>
                                        <div className="nc-input-wrapper">
                                            <span className="nc-input-icon"><FontAwesomeIcon icon={faEnvelope} /></span>
                                            <input 
                                                type="email" 
                                                id="email" 
                                                name="email" 
                                                placeholder="Enter email address" 
                                                value={formData.email} 
                                                onChange={handleChange}
                                                required 
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Draft / Message Details */}
                                <div className="nc-form-field">
                                    <label htmlFor="message">Draft / Message Details <span className="req">*</span></label>
                                    <div className="nc-input-wrapper nc-textarea-wrapper">
                                        <span className="nc-input-icon nc-textarea-icon"><FontAwesomeIcon icon={faFileAlt} /></span>
                                        <textarea 
                                            id="message" 
                                            name="message" 
                                            rows="2" 
                                            placeholder="Paste affidavit text or details e.g., 'Change name from A to B'" 
                                            value={formData.message} 
                                            onChange={handleChange}
                                            required
                                        ></textarea>
                                    </div>
                                </div>

                                {/* High-Impact CTA Button */}
                                <button type="submit" className="nc-form-submit-btn">
                                    <FontAwesomeIcon icon={faPaperPlane} /> Get Instant Quote & Free Assistance ➔
                                </button>

                                {/* Trust Guarantee Micro-Banner */}
                                <div className="nc-form-trust">
                                    <span><FontAwesomeIcon icon={faShieldAlt} /> 100% Confidential</span>
                                    <span className="dot">•</span>
                                    <span>Passport & Gazette Compliant</span>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Decorative floating elements */}
                <div className="nc-hero-deco nc-deco-1"></div>
                <div className="nc-hero-deco nc-deco-2"></div>
                <div className="nc-hero-deco nc-deco-3"></div>
            </header>

            {/* ===== NEWSPAPER TICKER SECTION ===== */}
            <section className="nc-ticker-section">
                <p className="nc-ticker-label">PUBLISHED ACROSS &nbsp;·&nbsp; <span className="ticker-state">TAMIL NADU</span> &nbsp;·&nbsp; <span className="ticker-state">ANDHRA</span> &nbsp;·&nbsp; <span className="ticker-state">KERALA</span> &nbsp;·&nbsp; <span className="ticker-state">KARNATAKA</span></p>
                <div className="nc-ticker-track-wrap">
                    <div className="nc-ticker-track">
                        {/* Duplicated for seamless loop */}
                        {[...Array(2)].map((_, loopIdx) => (
                            <div className="nc-ticker-items" key={loopIdx}>
                                {[
                                    { name: "Times of India", lang: "EN" },
                                    { name: "The Hindu", lang: "EN" },
                                    { name: "Indian Express", lang: "EN" },
                                    { name: "Dinathanthi", lang: "TM" },
                                    { name: "Dinamalar", lang: "TM" },
                                    { name: "Dinamani", lang: "TM" },
                                    { name: "Dinakaran", lang: "TM" },
                                    { name: "Hindu Tamil Thisai", lang: "TM" },
                                    { name: "Malai Malar", lang: "TM" },
                                    { name: "Daily Thanthi", lang: "TM" },
                                    { name: "Eenadu", lang: "TE" },
                                    { name: "Sakshi", lang: "TE" },
                                    { name: "Mathrubhumi", lang: "ML" },
                                    { name: "Malayala Manorama", lang: "ML" },
                                    { name: "Vijay Karnataka", lang: "KN" },
                                    { name: "Deccan Herald", lang: "EN" },
                                ].map((paper, i) => (
                                    <span className="nc-ticker-item" key={i}>
                                        <span className="ticker-dot">✦</span>
                                        <span className="ticker-name">{paper.name}</span>
                                        <span className={`ticker-lang ticker-lang-${paper.lang.toLowerCase()}`}>{paper.lang}</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: How It Works — Premium Editorial Cards */}
            <section className="nc-section nc-process">
                <div className="nc-container">
                    
                    {/* Editorial Heading */}
                    <div className="nc-process-header">
                        <p className="nc-process-eyebrow">HOW THE NAME CHANGE PROCESS WORKS</p>
                        <h2 className="nc-process-heading">
                            Easy steps to publish your{" "}
                            <em className="nc-process-heading-highlight">Change of Name</em>{" "}
                            advertisement. Quick, simple, hassle-free.
                        </h2>
                    </div>

                    <div className="process-grid-v2">
                        {/* Card 01 — Light */}
                        <div className="pv2-card pv2-card-light">
                            <div className="pv2-card-top">
                                <span className="pv2-step-num">01</span>
                                <span className="pv2-deco-script">அ தி ரி யா ம யம</span>
                            </div>
                            <h3 className="pv2-card-title">Why publish a Change of Name advertisement?</h3>
                            <p className="pv2-card-desc">
                                Publishing a Change of Name advertisement in a newspaper helps create a public record of your name change. It is commonly required or recommended as part of the name change process for various legal, official and administrative purposes, depending on your specific requirement.
                            </p>
                        </div>

                        {/* Card 02 — Dark */}
                        <div className="pv2-card pv2-card-dark">
                            <div className="pv2-card-top">
                                <span className="pv2-step-num">02</span>
                                <span className="pv2-deco-script">அ தி ரி யா ம யம</span>
                            </div>
                            <h3 className="pv2-card-title">Leading Newspapers We Cover</h3>
                            <p className="pv2-card-desc">
                                Choose from leading English, Tamil and other regional newspapers. We'll help you select the right newspaper and edition for your requirement.
                            </p>
                        </div>

                        {/* Card 03 — Light */}
                        <div className="pv2-card pv2-card-light">
                            <div className="pv2-card-top">
                                <span className="pv2-step-num">03</span>
                                <span className="pv2-deco-script">அ தி ரி யா ம யம</span>
                            </div>
                            <h3 className="pv2-card-title">Share Your Details</h3>
                            <p className="pv2-card-desc">
                                Share your old name, new name, father's / husband's name (S/o, D/o, or W/o), date of birth, place of birth and residential address. We'll prepare your advertisement and guide you through the entire process.
                            </p>
                        </div>

                        {/* Card 04 — Dark */}
                        <div className="pv2-card pv2-card-dark">
                            <div className="pv2-card-top">
                                <span className="pv2-step-num">04</span>
                                <span className="pv2-deco-script">அ தி ரி யா ம யம</span>
                            </div>
                            <h3 className="pv2-card-title">Advertisement Published</h3>
                            <p className="pv2-card-desc">
                                After your approval, your Change of Name advertisement will be published in the selected newspaper. Published copies and e-paper will be provided, subject to availability.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== NEWSPAPERS WE COVER — Premium Campaign Section ===== */}
            <section className="nc-newspapers-section">
                <div className="nc-newspapers-inner">
                    
                    {/* Section Header */}
                    <div className="nc-newspapers-header">
                        <span className="nc-newspapers-badge">NEWSPAPERS WE COVER</span>
                        <h2 className="nc-newspapers-title">
                            Leading English and Tamil dailies —<br />
                            <span className="nc-newspapers-title-accent">grouped by language.</span>
                        </h2>
                        <p className="nc-newspapers-subtitle">
                            Choose the paper that best suits your requirement, or let our team recommend the right newspaper and edition for you.
                        </p>
                    </div>

                    {/* Newspaper Grid — English */}
                    <div className="nc-newspaper-group">
                        <div className="nc-group-header">
                            <h3 className="nc-group-label">English</h3>
                            <span className="nc-group-count">6 DAILIES</span>
                        </div>
                        <div className="nc-logo-grid">
                            {[
                                { name: "The Hindu", img: "thehindu" },
                                { name: "Times of India", img: "timesofindia" },
                                { name: "Indian Express", img: "indianexpress" },
                                { name: "DT Next", img: "dtnext" },
                                { name: "Trinity Mirror", img: "trinitymirror" },
                                { name: "Deccan Chronicle", img: "deccanchronicle" }
                            ].map((paper, i) => (
                                <div className="nc-logo-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
                                    <div className="nc-logo-card-inner">
                                        <span className="nc-logo-name">{paper.name}</span>
                                    </div>
                                    <div className="nc-logo-card-glow"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Newspaper Grid — Tamil */}
                    <div className="nc-newspaper-group">
                        <div className="nc-group-header">
                            <h3 className="nc-group-label">Tamil</h3>
                            <span className="nc-group-count">8 DAILIES</span>
                        </div>
                        <div className="nc-logo-grid">
                            {[
                                { name: "Daily Thanthi", img: "dailythanthi" },
                                { name: "Dinamalar", img: "dinamalar" },
                                { name: "Dinamani", img: "dinamani" },
                                { name: "Dinakaran", img: "dinakaran" },
                                { name: "Hindu Tamil Thisai", img: "hindutamil" },
                                { name: "Malai Malar", img: "malaimalar" },
                                { name: "Dina Thinum", img: "dinathinum" },
                                { name: "Makkal Kural", img: "makkalkural" }
                            ].map((paper, i) => (
                                <div className="nc-logo-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
                                    <div className="nc-logo-card-inner">
                                        <span className="nc-logo-name">{paper.name}</span>
                                    </div>
                                    <div className="nc-logo-card-glow"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== WHY CHOOSE US — Vibrant Stats + Features ===== */}
            <section className="nc-why-section">
                <div className="nc-why-inner">
                    
                    {/* Animated Stats Strip */}
                    <div className="nc-stats-strip">
                        {[
                            { number: "35+", label: "Years of Trust", icon: "🏆" },
                            { number: "100+", label: "Newspapers Covered", icon: "📰" },
                            { number: "150K+", label: "Ads Published", icon: "✅" },
                            { number: "24hr", label: "Fast Turnaround", icon: "⚡" }
                        ].map((stat, i) => (
                            <div className="nc-stat-card" key={i} style={{ animationDelay: `${i * 0.15}s` }}>
                                <span className="nc-stat-icon">{stat.icon}</span>
                                <span className="nc-stat-number">{stat.number}</span>
                                <span className="nc-stat-label">{stat.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Why Choose Us Features */}
                    <div className="nc-features-grid">
                        <div className="nc-features-left">
                            <span className="nc-features-badge">WHY CHOOSE US</span>
                            <h2 className="nc-features-title">
                                Trusted by <em>thousands</em> across Tamil Nadu for name change advertisements.
                            </h2>
                            <p className="nc-features-desc">
                                With over three decades of experience, Sri Sairam Advertising ensures your name change ad is published legally, quickly, and affordably in the newspaper of your choice.
                            </p>
                            <button className="nc-features-cta" onClick={scrollToForm}>
                                Book Your Ad Now &nbsp; ➔
                            </button>
                        </div>

                        <div className="nc-features-right">
                            {[
                                { icon: "🔒", title: "Legal Format Guaranteed", desc: "All ads follow passport office and gazette standards." },
                                { icon: "💰", title: "Lowest Price Promise", desc: "Competitive rates with no hidden charges or surprises." },
                                { icon: "🚀", title: "Same-Day Booking", desc: "Submit today, published in the next available edition." },
                                { icon: "📋", title: "Complete Assistance", desc: "From draft to publication, we handle everything for you." }
                            ].map((feat, i) => (
                                <div className="nc-feature-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                                    <span className="nc-feature-icon">{feat.icon}</span>
                                    <div className="nc-feature-text">
                                        <h4>{feat.title}</h4>
                                        <p>{feat.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SAMPLE FORMATS — How your ad appears in print ===== */}
            <section className="nc-samples-section">
                <div className="nc-samples-inner">
                    <div className="nc-samples-header">
                        <span className="nc-samples-eyebrow">SAMPLE FORMATS · ILLUSTRATIVE ONLY</span>
                        <div className="nc-samples-header-split">
                            <h2 className="nc-samples-title">
                                How your <em>advertisement</em> appears in print.
                            </h2>
                            <p className="nc-samples-desc">
                                See how your Change of Name advertisement will appear in leading newspapers. Formats may vary depending on the newspaper and language selected.
                            </p>
                        </div>
                    </div>

                    <div className="nc-samples-grid">
                        {/* English Format */}
                        <div className="nc-sample-card">
                            <div className="nc-sample-card-header">
                                <span className="nc-sample-format-label">ENGLISH DAILY · SAMPLE FORMAT</span>
                                <span className="nc-sample-dot">■</span>
                                <span className="nc-sample-tag">SAMPLE</span>
                            </div>
                            <h3 className="nc-sample-ad-title">CHANGE OF NAME</h3>
                            <p className="nc-sample-ad-body">
                                I, [New Name] S/o, D/o, W/o [Father / Husband name], DOB [DD.MM.YYYY], residing at [Full address], have changed my name from [Old Name] to [New Name] for all future purposes.
                            </p>
                            <div className="nc-sample-card-footer">
                                <span className="nc-sample-footer-label">Format: <em>Illustrative</em></span>
                                <span className="nc-sample-footer-brand">Sri Sairam Advertising</span>
                            </div>
                        </div>

                        {/* Tamil Format */}
                        <div className="nc-sample-card">
                            <div className="nc-sample-card-header">
                                <span className="nc-sample-format-label">TAMIL DAILY · SAMPLE FORMAT</span>
                                <span className="nc-sample-dot">■</span>
                                <span className="nc-sample-tag">SAMPLE</span>
                            </div>
                            <h3 className="nc-sample-ad-title nc-sample-tamil-title">பெயர் மாற்றம்</h3>
                            <p className="nc-sample-ad-body">
                                நான், [பழைய பெயர்] இனிமேல் [புதிய பெயர்] என்று அறியப்படுவேன். பிறப்பு தேதி [DD. MM.YYYY] முகவரி: [முழு முகவரி].
                            </p>
                            <div className="nc-sample-card-footer">
                                <span className="nc-sample-footer-label">Format: <em>Illustrative</em></span>
                                <span className="nc-sample-footer-brand">Sri Sairam Advertising</span>
                            </div>
                        </div>

                        {/* Public Notice Format */}
                        <div className="nc-sample-card">
                            <div className="nc-sample-card-header">
                                <span className="nc-sample-format-label">PUBLIC NOTICE · SAMPLE FORMAT</span>
                                <span className="nc-sample-dot">■</span>
                                <span className="nc-sample-tag">SAMPLE</span>
                            </div>
                            <h3 className="nc-sample-ad-title">PUBLIC NOTICE</h3>
                            <p className="nc-sample-ad-body">
                                This is to notify that [Old Name] has changed her/his name to [New Name] after marriage / as per the requirement stated in the affidavit dated [date]. All concerned may take note.
                            </p>
                            <div className="nc-sample-card-footer">
                                <span className="nc-sample-footer-label">Format: <em>Illustrative</em></span>
                                <span className="nc-sample-footer-brand">Sri Sairam Advertising</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== WHY SRI SAIRAM — Trust Section ===== */}
            <section className="nc-trust-section">
                <div className="nc-trust-inner">
                    <div className="nc-trust-top">
                        <div className="nc-trust-left">
                            <span className="nc-trust-eyebrow">WHY SRI SAIRAM ADVERTISING</span>
                            <h2 className="nc-trust-title">
                                A <em>trusted</em> newspaper advertising agency for your Change of Name.
                            </h2>
                        </div>
                        <div className="nc-trust-right">
                            <div className="nc-trust-quote-card">
                                <span className="nc-trust-quote-icon">✦</span>
                                <p className="nc-trust-quote-text">"A new name — a new beginning."</p>
                                <p className="nc-trust-quote-sub">Serving families across Tamil Nadu since 1988</p>
                            </div>
                        </div>
                    </div>

                    <div className="nc-trust-benefits">
                        {[
                            { icon: "₹", title: "Best Newspaper Rates", desc: "Competitive advertisement pricing across every daily we cover." },
                            { icon: "📰", title: "Wide Newspaper Coverage", desc: "Leading English & regional newspapers under one roof." },
                            { icon: "🤝", title: "Professional Assistance", desc: "Guidance throughout the booking process — from draft to publish." },
                            { icon: "💬", title: "Quick Response", desc: "Phone, WhatsApp & email support whenever you need us." }
                        ].map((item, i) => (
                            <div className="nc-trust-benefit-card" key={i}>
                                <div className="nc-trust-benefit-icon">
                                    <span>{item.icon}</span>
                                </div>
                                <h4 className="nc-trust-benefit-title">{item.title}</h4>
                                <p className="nc-trust-benefit-desc">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== GET IN TOUCH — Floating CTA Card ===== */}
            <section className="nc-git-section">
                <div className="nc-git-card">
                    {/* Left Column: Heading & Availability */}
                    <div className="nc-git-left">
                        <div className="nc-git-eyebrow">
                            <span className="nc-git-eyebrow-line"></span>
                            <span>GET IN TOUCH</span>
                        </div>
                        <h2 className="nc-git-title">
                            Ready to publish your<br />
                            <em>Change of Name advertisement?</em>
                        </h2>
                        <p className="nc-git-desc">
                            Need help choosing the right newspaper or knowing the advertisement cost? Our team is here to assist you.
                        </p>
                        <div className="nc-git-status">
                            <span className="nc-git-status-dot"></span>
                            <span className="nc-git-status-text">Our team is available now — reply within minutes.</span>
                        </div>
                    </div>

                    {/* Right Column: Contact Action Buttons */}
                    <div className="nc-git-right">
                        {/* WhatsApp Action Button */}
                        <a 
                            href="https://wa.me/919500037429?text=Hi%2C%20I%20want%20to%20publish%20a%20Change%20of%20Name%20advertisement." 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="nc-git-btn nc-git-btn-whatsapp"
                        >
                            <div className="nc-git-btn-icon nc-git-icon-wa">
                                <FontAwesomeIcon icon={faWhatsapp} />
                            </div>
                            <div className="nc-git-btn-text">
                                <span className="nc-git-btn-label">WHATSAPP NOW (FASTEST)</span>
                                <span className="nc-git-btn-val">95000 37429</span>
                            </div>
                            <div className="nc-git-btn-arrow">➜</div>
                        </a>

                        {/* Call Action Button */}
                        <a 
                            href="tel:9500037429" 
                            className="nc-git-btn nc-git-btn-call"
                        >
                            <div className="nc-git-btn-icon nc-git-icon-call">
                                <FontAwesomeIcon icon={faPhone} />
                            </div>
                            <div className="nc-git-btn-text">
                                <span className="nc-git-btn-label">CALL NOW</span>
                                <span className="nc-git-btn-val">95000 37429</span>
                            </div>
                            <div className="nc-git-btn-arrow">➜</div>
                        </a>

                        {/* Email Action Button */}
                        <a 
                            href="mailto:sai29ads@gmail.com" 
                            className="nc-git-btn nc-git-btn-email"
                        >
                            <div className="nc-git-btn-icon nc-git-icon-email">
                                <FontAwesomeIcon icon={faEnvelope} />
                            </div>
                            <div className="nc-git-btn-text">
                                <span className="nc-git-btn-label">EMAIL US</span>
                                <span className="nc-git-btn-val">sai29ads@gmail.com</span>
                            </div>
                            <div className="nc-git-btn-arrow">➜</div>
                        </a>
                    </div>
                </div>
            </section>


            {/* ===== DARK EDITORIAL FAQ SECTION ===== */}
            <section className="nc-faq-dark-section">
                <div className="nc-faq-container">
                    {/* Left Column: Heading & Contact Subtitle */}
                    <div className="nc-faq-left">
                        <div className="nc-faq-eyebrow">
                            <span className="nc-faq-eyebrow-line"></span>
                            <span>FREQUENTLY ASKED QUESTIONS</span>
                        </div>
                        <h2 className="nc-faq-title">
                            Answers to what<br />
                            <em>most clients</em> ask before<br />
                            booking.
                        </h2>
                        <p className="nc-faq-subtitle">
                            Still unsure? Call <a href="tel:9500037429" className="nc-faq-phone">+91 95000 37429</a>. Our team is happy to help.
                        </p>
                    </div>

                    {/* Right Column: 9 Accordion FAQ Items */}
                    <div className="nc-faq-right">
                        <div className="nc-faq-list">
                            {faqs.map((faq, index) => (
                                <div 
                                    key={index} 
                                    className={`nc-faq-item ${activeFaq === index ? "open" : ""}`}
                                    onClick={() => toggleFaq(index)}
                                >
                                    <div className="nc-faq-question">
                                        <h4 className="nc-faq-q-text">{faq.question}</h4>
                                        <span className="nc-faq-icon">{activeFaq === index ? "⌃" : "⌄"}</span>
                                    </div>
                                    {activeFaq === index && (
                                        <div className="nc-faq-answer">
                                            <p>{faq.answer}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>



            {/* Bottom Sticky Section */}
            <div className="bottom-sticky-bar">
                <p>Have questions about newspaper layouts and rates?</p>
                <button className="sticky-whatsapp-btn" onClick={handleWhatsAppDirect}>
                    <FontAwesomeIcon icon={faWhatsapp} /> Chat on WhatsApp
                </button>
            </div>
        </div>
    );
};

export default NameChange;
